import { PROJECT_TYPES } from '@/constants';
import {
  MemberPeerEvaluationDTO,
  MemberRatingDrops,
  ProjectAssessmentAverageDropsDTO,
  ProjectAssessmentDTO,
  ProjectAssessmentModelDTO,
  UserEngagementDTO
} from '@/models';

export const getAverageSatAndComplexityByAssessmentId = (
  averageSatAndComplexity: ProjectAssessmentAverageDropsDTO[],
  assessmentId: number
): { averageSat: number; averageComplexity: number } => {
  const selectedASAC = averageSatAndComplexity.find(asnc => asnc.assessmentPeriod.id === assessmentId);
  return {
    averageSat: selectedASAC?.satAverage || 3,
    averageComplexity: selectedASAC?.complexityAverage || 3
  };
};

export const getAveragePeerRatingPoint = (peerEvaluation: MemberPeerEvaluationDTO | MemberRatingDrops): number => {
  return (
    peerEvaluation.accountabilityPoint * 0.19 +
    peerEvaluation.professionalConductPoint * 0.22 +
    peerEvaluation.communicationPoint * 0.21 +
    peerEvaluation.personalAttributesPoint * 0.2 +
    peerEvaluation.supportivenessPoint * 0.18
  );
};

export const getActualEffort = (
  projectType: string,
  plannedEffort: number,
  userEngagements: UserEngagementDTO[]
): number => {
  if (projectType === PROJECT_TYPES.FIXED) {
    return +plannedEffort;
  }
  if (userEngagements?.length > 0) {
    return userEngagements.reduce((total, ue) => total + (ue.contribution || 0), 0) || 0;
  }
  return 0;
};

export const getProjectBaseValue = (data: ProjectAssessmentDTO | ProjectAssessmentModelDTO): number => {
  const { plannedEffort, key } = data;
  return plannedEffort * key;
};

export const getAddedValue = (
  data: ProjectAssessmentDTO | ProjectAssessmentModelDTO,
  averageComplexity: number,
  averageSat: number,
  actualEffort: number
): number => {
  const { plannedEffort, key, sat, complexity } = data;
  const operand1 = 0.2 * ((sat - averageSat) / 5);
  const operand2 = 0.4 * ((plannedEffort - actualEffort) / plannedEffort);
  const operand3 = 0.4 * ((complexity - averageComplexity) / 5);
  const result = plannedEffort * key * 0.2 * (operand1 + operand2 + operand3);
  return Math.round(result);
};

export const getRatingDropsFromProjectPEs = (
  userId: number,
  projectPE: MemberPeerEvaluationDTO[]
): MemberRatingDrops => {
  let accountabilityPoint = 0;
  let professionalConductPoint = 0;
  let communicationPoint = 0;
  let personalAttributesPoint = 0;
  let supportivenessPoint = 0;
  const filteredPE = projectPE.filter(pe => pe.recipient.id === userId);
  filteredPE.forEach(pe => {
    accountabilityPoint += pe.accountabilityPoint || 0;
    professionalConductPoint += pe.professionalConductPoint || 0;
    communicationPoint += pe.communicationPoint || 0;
    personalAttributesPoint += pe.personalAttributesPoint || 0;
    supportivenessPoint += pe.supportivenessPoint || 0;
  });
  const totalPE = filteredPE.length;
  return {
    accountabilityPoint: accountabilityPoint / totalPE,
    professionalConductPoint: professionalConductPoint / totalPE,
    communicationPoint: communicationPoint / totalPE,
    personalAttributesPoint: personalAttributesPoint / totalPE,
    supportivenessPoint: supportivenessPoint / totalPE
  };
};

export const getReceivedDropsFromProjectPEs = (userId: number, projectPE: MemberPeerEvaluationDTO[]): number => {
  return projectPE
    .filter(peerEvaluation => peerEvaluation.recipient?.id === userId)
    .reduce((acc, curr) => acc + curr.givingDrops, 0);
};

export const getFinalIndividualDrops = (userEngagement: UserEngagementDTO, receivedDrops: number): number => {
  const drops = userEngagement.contributionDrops - userEngagement.givingDrops;
  const summaryDrops = drops + receivedDrops;
  return userEngagement.adjustedDrops > 0 ? drops + userEngagement.adjustedDrops : summaryDrops;
};
